import { General } from "../../siteManagement/general.js";
import { FAIcon } from "../core/components/faIcon.js";
import { Config } from "../core/config/config.js";
import { Helper } from "../core/util/helper.js";
import { HTTPService, JsonResultClass } from "../core/util/httpManager.js";
import { TicketView } from "./views/ticket.js";
import { AssignmentView } from "./views/assignment.js";
import { Button } from "../core/components/button.js"
import { EntryType, Menu } from "../core/components/menu.js";
import { createSheetHandler } from "../core/util/pageUnloadHandler.js";
import { SmartSearchList, SmartSearchListProps } from "./smartSearch.js";
import { NotificationHelper } from "../core/util/notificationHelper.js";
import { Dialog, DialogAction } from "../core/components/dialog.js";
import { SheetEventType } from "../../siteManagement/sheet.js";
import { SmartSearchAutoComplete } from "./smartSearchAutoComplete.js";
import { CancelTokenSource } from "axios";
import { CheckBox } from "../core/components/checkBox.js";
import { ComponentHelper } from "../core/util/componentHelper.js";
import React from "react";
import axios from "axios";

export class SmartSearchSearchResultPreview extends React.Component<SmartSearchSearchResultPreviewProps, { views: SmartSearchViews, propsHash: number, initialized: boolean, selectedBookmark?: Bookmark }> {
    constructor(props: SmartSearchSearchResultPreviewProps) {
        super(props)
        this.state = {
            views: {
                AssignmentViews: {},
                TicketViews: {}
            },
            propsHash: Helper.getHashCode(JSON.stringify(props.searchResult)),
            initialized: false
        }
    }

    private handleTitleClicked = (b: Bookmark) => {
        this.setState({ selectedBookmark: b });
    }

    private renderDocumentPreview = (b: Bookmark) => {
        const is_favorite = Helper.hasValue(this.props.favoriteIds().find(f => f === b.id));
        switch (b.type) {
            case "pdf":
                return <SmartSearchPngPreviewThumbnail
                    key={b.id}
                    id={this.props.id}
                    favoriteIds={this.props.favoriteIds}
                    textSearch={this.props.textSearch}
                    bookmark={b}
                    showMenu={this.props.showMenu}
                    onClickFavorite={this.props.onClickFavorite}
                    collapseAllSheets={this.props.collapseAllSheets}
                    isFavorite={is_favorite}
                    isSelected={b.id == this.state?.selectedBookmark?.id}
                    onTitleClick={this.handleTitleClicked}
                    readOnly={this.props.readOnly}
                    originalConcepts={this.props.originalConcepts}
                />;
            case "db_entry":
                return (
                    <SearchResult key={b.id}
                        sourcelabel={null}
                        bookmark={b}
                        title={b.title}
                        subTitle={this.getSubtitle(b)}
                        onClickFavorite={this.props.onClickFavorite}
                        isFavorite={is_favorite}
                        onTitleClick={this.handleTitleClicked}
                        isSelected={b.id == this.state?.selectedBookmark?.id}
                        readOnly={this.props.readOnly}>
                        {this.getInnerHTML(b)}
                    </SearchResult>
                );
            default:
                return null;
        }
    }

    private getSubtitle = (bookmark: Bookmark): string => {
        if (!Helper.hasValue(bookmark) || !this.state.initialized || !Helper.hasValue(this.state.views)) {
            return bookmark.sourceLabel + ":";
        }
        const type = SmartSearchHelper.getType(bookmark.mimeType);
        switch (type) {
            case "ticket":
                const ticket = this.state.views.TicketViews[bookmark.isguid];
                return `${Helper.getTranslation("BrowserTitle.Ticket.Available")}: ${ticket?.ProblemNr ?? ''}`;
            case "einsatz":
                const assignment = this.state.views.AssignmentViews[bookmark.isguid];
                return `${Helper.getTranslation("BrowserTitle.Assigment.Available")}: ${assignment?.ProjNr ? (assignment?.ProjNr + "/" + assignment?.PIndex) : ''}`;
            default:
                return bookmark.sourceLabel + ":";
        }
    }

    private getInnerHTML = (bookmark: Bookmark) => {
        if (!Helper.hasValue(bookmark) || !this.state.initialized || !Helper.hasValue(this.state.views))
            return <></>;

        const type = SmartSearchHelper.getType(bookmark.mimeType);
        switch (type) {
            case "ticket":
                const ticket = this.state.views.TicketViews[bookmark.isguid];
                return (
                    <>
                        <TicketView ticket={ticket} bookmark={bookmark} />
                    </>
                );
            case "einsatz":
                const assignment = this.state.views.AssignmentViews[bookmark.isguid];
                return (
                    <>
                        <AssignmentView assignment={assignment} />
                    </>
                );
            default:
                return <></>;
        }
    }

    private loadViews = async () => {
        const props = this.props.searchResult.bookmarks.map((bookmark) => {
            return {
                IsGuid: bookmark.isguid,
                Type: SmartSearchHelper.getType(bookmark.mimeType)
            }
        });
        this.setState({ initialized: false, propsHash: Helper.getHashCode(JSON.stringify(this.props.searchResult)) });

        const result = await HTTPService.noBusyIndicator().post<JsonResultClass>("Smartsearch/GetViews", { props: props });

        this.setState({ views: result.Data, propsHash: Helper.getHashCode(JSON.stringify(this.props.searchResult)), initialized: true });
    }

    componentDidUpdate() {
        const hash = Helper.getHashCode(JSON.stringify(this.props.searchResult));
        if (hash != this.state.propsHash)
            this.loadViews();
    }

    componentDidMount() {
        this.loadViews();
    }

    private renderEmpty = () => {
        if (!this.props.showNoResultsMessage) {
            return null;
        }
        return (
            <div className={"smartSearch__empty"}>
                <div>
                    <label>
                        {Helper.getTranslation("SmartSearch.NoResults")}
                    </label>
                </div>
            </div>
        );
    }

    render() {
        if (this.props.searchResult == null || this.props.searchResult.bookmarks == null || !this.state.initialized) {
            return null;
        }
        const bookmarks = this.props.searchResult.bookmarks;

        return bookmarks.length == 0 ? this.renderEmpty() : (
            <div style={{ display: "flex", flex: "1", flexDirection: "column" }}>
                <div className={"smartSearch__result"}>
                    {bookmarks.map(this.renderDocumentPreview)}
                    {this.props.hasNext ? <LoadAdditionalData onLoadNext={this.props.onLoadNext}></LoadAdditionalData> : null}
                    {new Array(8).fill(0).map((_, index) => { return <div key={index} className="search-placeholder"></div> })}
                </div>
            </div>
        );
    }
}

interface SmartSearchViews {
    TicketViews: {};
    AssignmentViews: {}
}

export interface SmartSearchSearchResultPreviewProps extends React.PropsWithChildren {
    id: string,
    favoriteIds: () => string[];
    searchResult: ApiSearchResult;
    showMenu: boolean;
    onClickFavorite: (b: Bookmark) => void;
    readOnly: boolean;
    textSearch?: string[];
    showNoResultsMessage?: boolean;
    originalConcepts?: SmartSearchAPiCompletitionResult[];
    hasNext?: boolean;
    onLoadNext?: any;
    collapseAllSheets: () => void;
}

class SmartSearchPngPreviewThumbnail extends React.Component<{
    id: string, favoriteIds: () => string[], bookmark: Bookmark, textSearch: string[], onClickFavorite(b: Bookmark), showMenu: boolean, isFavorite: boolean, readOnly: boolean, onTitleClick: (b: Bookmark) => void, isSelected: boolean,
    originalConcepts?: SmartSearchAPiCompletitionResult[],
    collapseAllSheets: () => void;
}, { fromPagePercent: number, thumbnail: any }> {
    private searchResultRef: SearchResult;
    constructor(props) {
        super(props);
        this.state = { thumbnail: null, fromPagePercent: 0 };
    }

    async componentDidMount() {
        const result = await HTTPService.noBusyIndicator().get<any>("SmartSearchPreview/Thumbnail", { id: this.props.bookmark.id });
        this.setState({ thumbnail: result.thumbnail, fromPagePercent: result.fromPagePercent });
    }

    private handleOpenPdfPreview = (event) => {
        const sheet = General.Sheet.Manager.createSheet();
        sheet.setTitle(this.props.bookmark.path);
        sheet.show(() => {
            sheet.mountContent(SmartSearchPdfPreviewWrapper, {
                id: this.props.id,
                favoriteIds: this.props.favoriteIds,
                bookmark: this.props.bookmark,
                pageWidth: 800,
                textSearch: this.props.textSearch,
                showMenu: this.props.showMenu,
                onAddFavorite: this.props.onClickFavorite,
                onClickFavorite: this.props.onClickFavorite,
                isFavorite: this.props.isFavorite,
                originalConcepts: this.props.originalConcepts,
                collapseAllSheets: this.props.collapseAllSheets

            } as SmartSearchPdfPreviewProps);
        });
    }

    private handleImageLoaded = (e) => {
        const height: number = e.target.height;
        this.searchResultRef.scrollTop(height * (this.state.fromPagePercent ?? 0) / 100);
    }

    private extractFilenameFromPath = (path: string) => {
        try {
            const filename = path.split('\\').pop().split('/').pop();
            if (!filename) {
                return path;
            }

            return filename;
        } catch (error) {
            return path;
        }
    }

    render() {
        const thumbnail = this.state.thumbnail ? < img style={{ display: "block", marginLeft: "auto", marginRight: "auto", cursor: "pointer" }} src={"data:image/png;base64, " + this.state.thumbnail} onClick={this.handleOpenPdfPreview} onLoad={this.handleImageLoaded} /> : null;

        return <SearchResult
            title={this.props.bookmark.title}
            subTitle={this.extractFilenameFromPath(this.props.bookmark.subTitle)
            }
            sourcelabel={this.props.bookmark.sourceLabel}
            ref={(e) => this.searchResultRef = e}
            bookmark={this.props.bookmark}
            onClickFavorite={this.props.onClickFavorite}
            onTitleClick={this.props.onTitleClick}
            isFavorite={this.props.isFavorite}
            isSelected={this.props.isSelected}
            readOnly={this.props.readOnly}>
            <div style={{ height: "100%" }}>
                {thumbnail}
            </div>
        </SearchResult>
    }
}


interface SmartSearchPdfPreviewProps {
    id: string,
    bookmark: Bookmark,
    textSearch?: string[],
    onClick?(),
    pageWidth?: any,
    showMenu?: boolean,
    onAddFavorite?: (bookmark: Bookmark) => Promise<boolean>,
    onRemoveFavorite?: (id: string[]) => Promise<boolean>,
    onClickFavorite: any;
    isFavorite: boolean,
    favoriteIds: () => string[],
    originalConcepts?: SmartSearchAPiCompletitionResult[],
    collapseAllSheets: () => void;
}
interface SmartSearchPdfPreviewInternalProps extends SmartSearchPdfPreviewProps {
    onconceptClicked?: any
}

interface SmartSearchPreiviewState {
    resource: string,
    page?: number,
    isFavorite: boolean,
    pdfData?: PdfData,
    showConceptDialog?: boolean,
    concepts?: { rating: number, text: string, uri: string }[],
    selectedText?: string,
    showPdfViewer: boolean,
}

export class SmartSearchPdfPreviewWrapper extends React.Component<SmartSearchPdfPreviewProps, SmartSearchPreiviewState> {

    constructor(props: SmartSearchPdfPreviewInternalProps) {
        super(props)
        this.state = {
            resource: null,
            page: 1,
            isFavorite: props.isFavorite,
            showPdfViewer: true,
        }
    }

    private OpenSearchAddNewConcepts = (selectedConcepts: SmartSearchAPiCompletitionResult[]) => {
        var transformedConcepts = [];
        for (var concept of selectedConcepts) {
            transformedConcepts.push({ ConceptURI: concept.ConceptURI, Type: concept.Type, Text: concept.Text })
        }
        this.setState({ showPdfViewer: false });

        const sheet = General.Sheet.Manager.createSheet();
        sheet.bind(SheetEventType.Close, (target) => {
            this.setState({ showPdfViewer: true });
        });
        sheet.show(() => {
            const props: SmartSearchListProps = {
                id: this.props.id,
                favoriteIds: this.props.favoriteIds,// this.state.favourites.map(f => f.Label),
                removeFavorite: this.props.onRemoveFavorite,//this.removeFavourite,
                addFavorite: this.props.onAddFavorite,
                onFavoriteClick: this.handleChildAddFavorit,
                initialMachine: null,//this.props.initialMachine,
                readOnly: false,
                initialConcepts: transformedConcepts,
                collapseAllSheets: this.props.collapseAllSheets
            };

            sheet.mountContent(SmartSearchList, props);
            sheet.setTitle(Helper.getTranslation("SmartSearch.SmartSearch"));
        });

    }

    handleChildAddFavorit = async (b) => {
        await this.props.onClickFavorite(b);
        this.forceUpdate();
    }

    handleAddFavorit = async () => {
        await this.props.onAddFavorite(this.props.bookmark);
        this.forceUpdate();
    }



    render() {
        const isFavorite = Helper.hasValue(this.props.favoriteIds().find(f => f === this.props.bookmark.id))
        const props: SmartSearchPdfPreviewInternalProps = { ...this.props, onconceptClicked: this.OpenSearchAddNewConcepts, originalConcepts: this.props.originalConcepts };
        const pdfViewer = this.state.showPdfViewer ? <SmartSearchPdfPreviewInternal {...props}></SmartSearchPdfPreviewInternal> : null
        const menuItems = []
        if (this.props.showMenu) {
            menuItems.push({
                text: !isFavorite ? Helper.getTranslation("SmartSearch.AddFavorite") : Helper.getTranslation("SmartSearch.RemoveFavorite"),
                onClick: this.handleAddFavorit,
                show: true,
                icon: "star"
            });
        }
        menuItems.push({
            text: "Alle Sheets schließen",
            inContext: true,
            onClick: () => { this.props.collapseAllSheets(); },
            show: true,
            icon: "layer-group"
        });
        menuItems.push(
            {
                text: "Pfad kopieren",
                inContext: false,
                onClick: async () => {
                    await navigator.clipboard.writeText(this.props.bookmark.path);
                    NotificationHelper.showInfoMessage('Pfad wurde in die Zwischenablage eingefügt')
                },
                show: true,
                icon: "copy"
            });

        return <>{pdfViewer}

            {this.props ? <Menu entries={menuItems} onSelect={() => { }} enableMenuItem={() => true} handleEval={() => { }} /> : null}</>;
    }
}


export class SmartSearchPdfPreviewInternal extends React.Component<SmartSearchPdfPreviewInternalProps, SmartSearchPreiviewState> {
    private pdfContainer: HTMLDivElement;
    private pdfViewer: any;
    private autoComplete: SmartSearchAutoComplete;
    private OK_STATUS = 200;
    private lastCancelToken: CancelTokenSource;
    constructor(props: SmartSearchPdfPreviewInternalProps) {
        super(props)
        this.state = {
            resource: null,
            page: 1,
            isFavorite: props.isFavorite,
            showPdfViewer: true,
        }
    }

    private buildViewer = () => {
        const pdfData: any = {
            "resource": Config.baseUrl + "SmartSearchPreview/GetResourceByBookmark?id=" + encodeURIComponent(this.props.bookmark.id),//+ "SmartSearchPreview/Resource?path=" + encodeURIComponent(this.props.bookmark.pdfData.resource),
            "fromPage": this.state.pdfData.fromPage,
            "fromPagePercent": this.state.pdfData.fromPagePercent,
            "toPage": this.state.pdfData.toPage,
            "toPagePercent": this.state.pdfData.toPagePercent,
            "pageCount": this.state.pdfData.pageCount,
            "matches": this.state.pdfData.matches,
            "compressedPages": this.state.pdfData.compressedPages
        }
        // read data from rest interface and fill web component
        const pdfContainer = this.pdfContainer;
        const pdfViewer: any = document.createElement('pdf-viewer');
        this.pdfViewer = pdfViewer;
        pdfViewer.workerPath = Config.baseUrl + 'Scripts/smartSearch/pdf.worker.min.js';
        pdfViewer.src = {
            'url': pdfData.resource,
            "disableStream": true,
            "disableAutoFetch": true
        };
        pdfViewer.fromPage = pdfData.fromPage;
        pdfViewer.clipTop = pdfData.fromPagePercent;
        pdfViewer.toPage = pdfData.toPage;
        pdfViewer.clipBottom = pdfData.toPagePercent;
        pdfViewer.clipAll = true;
        pdfViewer.displayFlex = false; // show pages side-by-side (if possible)
        pdfViewer.initialFit = 'width'; // fit pages by 'height' or 'width'
        pdfViewer.clippingText = Helper.getTranslation("SmartSearch.ClickToShowCompleteDoc");
        pdfViewer.compressedPages = pdfData.compressedPages;
        pdfViewer.pageCount = pdfData.pageCount;
        pdfViewer.matches = pdfData.matches ?? [];
        pdfContainer.appendChild(pdfViewer);

        Helper.setBusyIndicator(true);

        document.addEventListener('pdf-document-loaded', this.handleDocumentLoaded);
        document.addEventListener('current-page', this.handlePageChangedScroll);
        document.addEventListener('concept-clicked', this.createConceptClickedHandler);
        document.addEventListener('pdf-internal-error', this.handlePdfError);
    }

    private handlePdfError = (e) => {
        const code = e.detail.code.id;
        if (code == 0) {
            NotificationHelper.showErrorMessage(Helper.getTranslation("WebSMS.Common.NotConnected"));
        }
        else {
            NotificationHelper.showErrorMessage(Helper.getTranslation("SmartSearch.ErrorLoadPdfPage"));
        }
        Helper.setBusyIndicator(false);
    }

    private createConceptClickedHandler = (e) => {
        console.log("createConceptClickedHandler");
        this.setState({ showConceptDialog: true, concepts: e.detail.concepts, selectedText: e.detail.elements[0].textContent });
    }

    private handleDocumentLoaded = () => {
        Helper.setBusyIndicator(false);
    }

    private handlePageChangedScroll = (e) => {
        this.setState({ page: e.detail.page })
    }

    async componentDidMount() {
        try {
            const pdfData: PdfData = await HTTPService.noBusyIndicator().get<any>("SmartSearchPreview/Bookmark", { id: this.props.bookmark.id });
            this.setState({ pdfData }, () => {
                this.buildViewer();
            })

        } catch (e) {
            Helper.setBusyIndicator(false);
        }
    }



    componentWillUnmount() {
        document.removeEventListener('pdf-document-loaded', this.handleDocumentLoaded);
        document.removeEventListener('current-page', this.handlePageChangedScroll);
        document.removeEventListener('concept-clicked', this.createConceptClickedHandler);
        document.removeEventListener('pdf-internal-error', this.handlePdfError);
    }

    handleClip = () => {
        this.pdfViewer.setClipping(true);
    }

    handleFitWidth = () => {
        this.pdfViewer.fitWidth();
    }

    handleFitHeight = () => {
        this.pdfViewer.fitHeight()
    }

    handleBestFit = () => {
        this.pdfViewer.actualFit();
    }

    handleZoomIn = () => {
        this.pdfViewer.zoomIn();
    }

    handleZoomOut = () => {
        this.pdfViewer.zoomOut();
    }

    handlePageChangePickList = (e) => {
        const page = Number(e.target.value);
        this.pdfViewer.jumpToPage(page);
        this.setState({ page: page })
    }

    renderPageNumbers = () => {
        const options = [];
        for (var i = 1; i <= this.state.pdfData.pageCount; i++) {
            options.push(<option key={i} value={i}>{i}</option>)
        }
        return options;
    }
    renderDialog = () => {
        if (!this.state.showConceptDialog) {
            return;
        }
        return <SmartSearchConceptClickedDialog
            selectedText={this.state.selectedText}
            originalConcepts={this.props.originalConcepts}
            afterClose={() => this.setState({ showConceptDialog: false })}
            onconceptClicked={this.props.onconceptClicked}
            clickedConcepts={this.state.concepts}
        ></SmartSearchConceptClickedDialog>
    }

    render() {
        if (!this.state.pdfData) {
            return null;
        }

        return (<>
            {this.renderDialog()}
            <div className="smartSearch__preview-actions">
                <div>
                    <Button
                        buttonStyle={{ marginLeft: "5px" }}
                        iconName="left-right"
                        handleClick={this.handleFitWidth}
                    />
                    <Button
                        buttonStyle={{ marginLeft: "5px" }}
                        iconName="up-down"
                        handleClick={this.handleFitHeight}
                    />
                    <Button
                        buttonStyle={{ marginLeft: "5px" }}
                        iconName="maximize"
                        handleClick={this.handleBestFit}
                    />
                    <Button
                        buttonStyle={{ marginLeft: "5px" }}
                        iconName="rotate-left"
                        handleClick={this.handleClip}
                    />
                </div>
                <div style={{ marginTop: "2px" }}>
                    <Button
                        buttonStyle={{ marginLeft: "5px" }}
                        iconName="magnifying-glass-plus"
                        handleClick={this.handleZoomIn}
                    />
                    <Button
                        buttonStyle={{ marginLeft: "5px" }}
                        iconName="magnifying-glass-minus"
                        handleClick={this.handleZoomOut}
                    />
                    <select style={{
                        border: "1px solid rgb(179, 179, 179)",
                        backgroundColor: "white",
                        height: "38px",
                        marginLeft: "5px",
                        width: "81px",
                        fontSize: "12pt",

                    }} onChange={this.handlePageChangePickList}
                        value={this.state.page}>
                        {this.renderPageNumbers()}
                    </select>
                </div>
            </div>
            {this.state.showPdfViewer ? <div onClick={this.props.onClick} style={{ height: "100%", overflow: "scroll", backgroundColor: "lightgray" }} ref={e => this.pdfContainer = e}></div> : null}

        </>);
    }
}

export class PdfPreview extends React.Component<{ resource: string }, any>{
    render() {
        const encoded = encodeURIComponent(`${Config.baseUrl}SmartSearchPreview/Resource?path=${encodeURIComponent(this.props.resource)}`);
        return (
            <iframe style={{ flex: 1, height: "100%", width: "100%", display: "block" }} src={`${Config.baseUrl}pdfviewer/web/viewer.html?file=${encoded}`} />
        )
    }
}

interface SearchResultProps extends React.PropsWithChildren {
    bookmark: Bookmark;
    onClickFavorite: (b: Bookmark) => void;
    isFavorite: boolean;
    readOnly: boolean;
    onTitleClick: (b: Bookmark) => void;
    isSelected: boolean;
    title: string;
    subTitle: string;
    sourcelabel: string;
}

class SearchResult extends React.Component<SearchResultProps, any> {
    private scrollableDiv: HTMLDivElement;
    private getIcon = () => {
        const type = SmartSearchHelper.getType(this.props.bookmark.mimeType);
        return SmartSearchHelper.getIcon(type);
    }

    public scrollTop = (top: number) => {
        this.scrollableDiv.scrollTop = top;
    }

    private handleFavClick = () => {
        if (this.props.readOnly)
            return;
        this.props.onClickFavorite(this.props.bookmark);
    }

    private handleTitleClick = () => {
        this.props.onTitleClick && this.props.onTitleClick(this.props.bookmark);
    }

    private getMarker = () => {
        if (this.props.readOnly)
            return;
        return (
            <>
                <FAIcon name="star" attributes={["2x"]} prefix={"fal"} style={{ color: "#171718", display: this.props.isFavorite ? "none" : "inline-block" }} />
                <FAIcon name="star" attributes={["2x"]} prefix={"fas"} style={{ color: "#ffd600", display: !this.props.isFavorite ? "none" : "inline-block", stroke: "black", strokeWidth: "35px", textShadow: "0 0 3px #f57f17" }} />
            </>
        );

    }

    private renderTitle = () => {
        if (Helper.isNullOrEmpty(this.props.subTitle)) {
            return (
                <>
                    <div style={{ gridColumn: "2/3", gridRow: "1/3", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", alignItems: "center", marginLeft: "5px", fontWeight: "bold", padding: "5px 0", display: "flex" }} onClick={this.handleTitleClick}>
                        {this.props.title}
                    </div>
                </>
            );
        } else {
            return (
                <>
                    <div style={{ gridColumn: "2/3", gridRow: "1/2", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", alignItems: "center", marginLeft: "5px", fontWeight: "bold", paddingTop: "5px", fontSize: "0.95em", textAlign: "left" }} onClick={this.handleTitleClick} title={this.props.title}>
                        {this.props.title}
                    </div>
                    <div style={{ gridColumn: "2/3", gridRow: "2/3", alignItems: "center", marginLeft: "5px", paddingBottom: "5px", fontSize: "0.95em", textAlign: "left" }} onClick={this.handleTitleClick} title={this.props.subTitle}>
                        <div style={{ display: "flex", lineHeight: "normal", alignItems: "center" }}>
                            {this.props.sourcelabel ? <div style={{ backgroundColor: "var(--primary-color)", color: "white", "padding": "4px 8px", textAlign: "center", borderRadius: "5px", fontSize: "smaller", "marginRight": "5px" }}>{this.props.sourcelabel}</div> : null}
                            <div style={{ textOverflow: "ellipsis", flex: 1, overflow: "hidden", whiteSpace: "nowrap" }}>{this.props.subTitle}</div>
                        </div>
                    </div>
                </>
            );
        }
    }

    render() {
        let className = "smartSearch__result__tile";
        if (this.props.isSelected) {
            className += " selected"
        }

        return (
            <div className={className} >
                <div style={{ display: "grid", backgroundColor: "#E5E5E8", gridTemplateColumns: "50px 1fr 50px", gridTemplateRows: "1fr 1fr", borderRadius: "var(--rounded-top)" }} >
                    <div style={{ gridColumn: "1/2", gridRow: "1/3", display: "flex", justifyContent: "center", alignItems: "center" }} onClick={this.handleTitleClick}>
                        <FAIcon name={this.getIcon()} attributes={["2x"]} />
                    </div>
                    {this.renderTitle()}
                    <div style={{ gridColumn: "3/4", gridRow: "1/3", display: "flex", justifyContent: "center", alignItems: "center", cursor: this.props.readOnly ? "default" : "pointer" }} onClick={this.handleFavClick}>
                        {this.getMarker()}
                    </div>
                </div>
                <div ref={(e) => this.scrollableDiv = e} style={{ overflow: this.props.isSelected ? "auto" : "hidden", gridColumn: "1/2", gridRow: "2/3" }}>
                    {this.props.children}
                </div>
            </div>
        );
    }
}

class LoadAdditionalData extends React.Component<{ onLoadNext?: any }, any> {

    render() {
        let className = "smartSearch__result__tile";

        return (
            <div className={className} style={{ backgroundColor: "rgb(229, 229, 232)" }}>
                <div style={{ overflow: "auto", gridColumn: "1/2", gridRow: "1/3" }}>
                    <a style={{ height: "100%", display: "block", alignContent: "center" }} onClick={this.props.onLoadNext}>Weitere Ergebnisse laden</a>
                </div>
            </div>
        );
    }
}

interface SmartSearchConceptClickedDialogProps {
    originalConcepts: SmartSearchAPiCompletitionResult[],
    selectedText: string,
    onconceptClicked: any,
    clickedConcepts: { rating: number, text: string, uri: string }[],
    afterClose?: () => void;
}
interface SmartSearchConceptClickedDialogState {
    checkedConcepts: SmartSearchCheckedConcept[],
    searchResult?: ApiSearchResult,
    searching: boolean,
    searchText?: string[],
    currentPage: number
}
interface SmartSearchCheckedConcept extends SmartSearchAPiCompletitionResult {
    selected: boolean
}
class SmartSearchConceptClickedDialog extends React.Component<SmartSearchConceptClickedDialogProps, SmartSearchConceptClickedDialogState>{

    constructor(props: SmartSearchConceptClickedDialogProps) {
        super(props)
        this.state = {
            checkedConcepts: this.props.originalConcepts.map((c) => {
                return { ...c, selected: true }
            }),
            searching: false,
            searchText: [""],
            currentPage: 0
        }
    }
    private factory = ComponentHelper.createFactory();
    private autoComplete: SmartSearchAutoComplete;
    private OK_STATUS = 200;
    private lastCancelToken: CancelTokenSource;

    private dialogActions: Array<DialogAction> = [
        {
            label: Config.translations.cancel,
            action: () => {

            },
        },
        {
            label: Config.translations.ok,
            action: () => {
                const selectedConcepts = this.autoComplete.getSelected();
                const checkedOldConcepts: SmartSearchAPiCompletitionResult[] = this.state.checkedConcepts.filter(c => c.selected);
                const combinedConcepts = selectedConcepts.concat(checkedOldConcepts)
                this.props.onconceptClicked(combinedConcepts);
            }
        },
    ]

    private handleSelectionChanged = () => {
        const values = this.autoComplete.getSelected();
        if (values.length == 0) {
            this.cancelRequest();
            this.setState({
                searchResult: {
                    bookmarks: [],
                    query: {
                        concept: []
                    }
                }, searching: false
            });
        } else {
            this.setState({ searching: true }, () => {
                this.executeSearch(values, values, 0);
            })
        }
    }

    private cancelRequest = () => {
        if (Helper.hasValue(this.lastCancelToken)) {
            this.lastCancelToken.cancel();
            this.lastCancelToken = null;
        }
    }

    private executeSearch = async (concepts: SmartSearchAPiCompletitionResult[], phrases: SmartSearchAPiCompletitionResult[], page: number) => {
        this.cancelRequest();

        const conceptsUris = concepts
            .filter(x => x.Type != "Text" && !Helper.isNullOrEmpty(x.ConceptURI))
            .map(x => x.ConceptURI);

        const phrasesUris = phrases
            .filter(x => x.Type == "Text" && !Helper.isNullOrEmpty(x.ConceptURI))
            .map(x => x.ConceptURI);

        const queryObject = { conceptURIs: conceptsUris, phrases: phrasesUris, limit: 25, page: page };


        const currentToken = axios.CancelToken.source();
        this.lastCancelToken = currentToken;

        try {
            Helper.setBusyIndicator(true).then(async _ => {
                const result = await axios.post<ApiSearchResult>(Config.baseUrl + "SmartSearchPreview/Start", queryObject, {
                    cancelToken: currentToken.token
                });

                if (typeof result.data == "string") {
                    //Wenn SS nicht erreichbar ist, bekommt man HTML durch einen Redirect.
                    NotificationHelper.showErrorMessage(Helper.getTranslation("WebSMS.Common.NotConnected"));
                }
                else if (this.lastCancelToken == currentToken && result.status == this.OK_STATUS) {
                    this.setState({ searchResult: result.data, searching: false, searchText: phrasesUris, currentPage: page });
                }
                Helper.setBusyIndicator(false);
            })

        } catch {
            if (this.lastCancelToken == currentToken) {
                //Abfrage wurde abgebrochen
            }
        }
    }

    private handleAfterClose = () => {
        if (this.props.afterClose) {
            this.props.afterClose();
        }
    }


    private renderCheckbox = (concept: SmartSearchAPiCompletitionResult) => {
        const props = this.factory.getDefaultCheckBoxProps();
        props.onChange = (_, value: boolean) => {
            const newCheckedConcepts = this.state.checkedConcepts.map((c) => ({
                ...c,
                selected: c.ConceptURI.match(concept.ConceptURI) ? value : c.selected
            }))
            this.setState({ checkedConcepts: newCheckedConcepts })
        }
        props.value = true;
        let typeText = concept.Type ? "(" + Helper.getTranslation(`SmartSearch.SearchType.${concept.Type.toLowerCase()}`) + ")" : "";
        let text = concept.Text || concept.ConceptURI;
        return (
            <div style={{ display: "flex", alignItems: "center", gap: "10px", paddingBottom: "10px" }}>
                <CheckBox {...props} />
                <label style={{ marginTop: "6px", fontWeight: "bold" }}>{typeText}</label><label style={{ marginTop: "6px" }}>{text}</label>
            </div>
        );
    }

    render() {
        //const transformedConcepts: SmartSearchAPiCompletitionResult[] = this.props.clickedConcepts.map((c) => {
        //    return { Type: "", ConceptURI: c.uri, Text: c.text }
        //})
        const originalConceptCheckboxes = this.props.originalConcepts.map((c) => {
            return this.renderCheckbox(c)
        })
        return <>
            <Dialog title={Helper.getTranslation("SmartSearch.SucheErweitern")}
                width="50%"
                actions={this.dialogActions} open={true} afterClose={this.handleAfterClose}>
                {originalConceptCheckboxes}
                <SmartSearchAutoComplete
                    autoClose={false}
                    ref={(e) => this.autoComplete = e}
                    initialValues={[]} //{transformedConcepts}
                    onSelectionChanged={this.handleSelectionChanged}
                    initialSearchText={this.props.selectedText}
                />
            </Dialog>
        </>

    }
}

export class SmartSearchHelper {

    public static getIsGuid = (url: string): string => {
        return new URL(url).searchParams.get("isguid");
    }

    public static getType = (mimeType: string) => {
        if (mimeType === "application/pdf") {
            return "pdf" as MimeType;
        }
        if (/^application\/ticket\+.+$/.test(mimeType)) {
            return "ticket" as MimeType;
        }
        if (/^application\/einsatz\+.+$/.test(mimeType)) {
            return "einsatz" as MimeType;
        }
        return "unbekannt" as MimeType;
    }

    public static getIcon = (mimeType: MimeType) => {
        switch (mimeType) {
            case "pdf":
                return "file-pdf";
            case "einsatz":
                return "calendar-circle-user";
            case "ticket":
                return "exclamation-square";
            case "unbekannt":
                return "question";

        }
    }
}

export type MimeType = "pdf" | "ticket" | "einsatz" | "unbekannt";

